// The ONE definition of what formatting an authored email body may
// contain, plus the pure helpers that don't need a parser.
//
// CONSUMER: both (Workers runtime AND the browser bundle)
//
// Why this file exists separately from the two sanitizers: an email body
// authored in the automation builder is sanitized TWICE — once in the
// browser (DOMPurify, as the author types/pastes) and once in the Worker
// (HTMLRewriter, at save and again at send). Two different parsers, one
// allowlist. Keeping the allowlist here means the two can't drift into
// disagreeing about what's safe, which is exactly how a sanitizer bypass
// gets born.
//
// DECISION: this list is deliberately TINY — the five things Grant asked
// for (bold, italic, underline, bullets, links) plus the paragraph
// structure needed to hold them. No style/class attributes, no colors,
// no fonts, no images, no tables. Every attribute that isn't named here
// is dropped, so the allowlist is closed, not "everything except the bad
// stuff". Contrast with signature-sanitizer.ts, which is deliberately
// permissive because a pasted Outlook signature legitimately needs table
// layouts and inline styles — that content comes from the employee's own
// mailbox, whereas this content is composed in a field and then mailed to
// ~19,000 clients.
//
// DECISION: <u> is included even though it's presentational, because
// "underline" is one of the five things advisors asked for and email
// clients render it reliably. <s>/<strike> are NOT included — nobody
// asked, and every tag is surface area.

/** Tags an authored email body may contain. Anything else is unwrapped
 *  (the tag disappears, its text content survives) rather than dropped
 *  outright, so an unsupported wrapper never silently eats a paragraph. */
export const RICH_TEXT_ALLOWED_TAGS = [
  "p",
  "br",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "ul",
  "ol",
  "li",
  "a",
] as const;

/** Attributes allowed, per tag. Every tag not listed here allows NOTHING
 *  — including `style`, `class`, and `id`. */
export const RICH_TEXT_ALLOWED_ATTRS: Record<string, readonly string[]> = {
  a: ["href", "target", "rel"],
};

/** URL schemes a link may use. A scheme-relative ("//host") or relative
 *  href is also rejected — an email body has no origin to resolve
 *  against, so a relative link is always a mistake. */
export const RICH_TEXT_ALLOWED_URL_SCHEMES = ["http:", "https:", "mailto:"] as const;

const ALLOWED_TAG_SET: ReadonlySet<string> = new Set(RICH_TEXT_ALLOWED_TAGS);

export function isAllowedRichTextTag(tag: string): boolean {
  return ALLOWED_TAG_SET.has(tag.toLowerCase());
}

/**
 * True when `url` is a link an email body may point at.
 *
 * Parsed with the URL constructor rather than string-matched, so the
 * classic bypasses ("java\tscript:", "JaVaScRiPt:", leading control
 * characters, "%6a%61vascript:") all normalize before the scheme is
 * compared. A value the parser rejects outright is rejected here too —
 * "unparseable" is never "safe".
 */
export function isAllowedRichTextUrl(url: string): boolean {
  const trimmed = url.trim();
  if (trimmed === "") return false;
  // Reject protocol-relative + root/path-relative hrefs before parsing:
  // they'd need a base URL to resolve, which an email has no notion of.
  if (trimmed.startsWith("//") || trimmed.startsWith("/")) return false;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return false;
  }
  return (RICH_TEXT_ALLOWED_URL_SCHEMES as readonly string[]).includes(
    parsed.protocol,
  );
}

/**
 * Which format an email action's stored `body` is in.
 *
 * ABSENCE MEANS "text", and that is load-bearing: every automation rule
 * authored before rich text existed stores a plain-text body with no
 * `body_format` key, and must keep rendering through the escape-and-
 * paragraph path byte-for-byte. Only a body the new editor wrote carries
 * `body_format: "html"`. Never infer the format by sniffing for tags —
 * a plain-text body that happens to mention "<3" would flip formats and
 * change what a client receives.
 */
export type RichTextBodyFormat = "text" | "html";

export function bodyFormatOf(value: unknown): RichTextBodyFormat {
  return value === "html" ? "html" : "text";
}

/** Escape a string so it can sit safely in HTML text content. */
export function escapeRichTextValue(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Render a PLAIN-TEXT email body as HTML: escape everything, blank lines
 * become paragraph breaks, single newlines become <br>.
 *
 * This is the canonical implementation. The engine's exported
 * `inlineEmailBodyToHtml` delegates here so the two can never drift —
 * which matters because opening an old plain-text rule in the rich-text
 * editor converts it through THIS function, and the converted body has
 * to render byte-identically to what the rule was already sending.
 * Otherwise merely opening a rule would silently change the email its
 * recipients get.
 */
export function plainTextToRichTextHtml(text: string): string {
  return escapeRichTextValue(text)
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
    .join("\n");
}

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  "#39": "'",
  nbsp: " ",
};

/** A code point String.fromCodePoint accepts — inside the Unicode range.
 *  `Number.isFinite` is NOT enough: 99999999 and 0x7FFFFFFF are finite
 *  but out of range, and fromCodePoint THROWS a RangeError on them. That
 *  throw, reached from richTextToPlainText / isRichTextEmpty inside the
 *  automation runner's per-tick loop (which has no per-action catch),
 *  would abort the ENTIRE tick — every rule, every company — and, since
 *  it throws before a run row is written, re-throw on every 15-minute
 *  tick forever. A body author controls the entity, so this is an
 *  employee-triggerable engine-wide DoS. Range-check first; a bad ref is
 *  left as the literal text it was. (Reported by the security review.) */
function isDecodableCodePoint(code: number): boolean {
  return Number.isInteger(code) && code >= 0 && code <= 0x10ffff;
}

/** Decode the small set of entities our own sanitizer + escaper emit.
 *  Used only when deriving the text/plain twin of an HTML body. Must
 *  NEVER throw — see isDecodableCodePoint. */
function decodeEntities(s: string): string {
  return s.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, name: string) => {
    const named = NAMED_ENTITIES[name.toLowerCase()];
    if (named !== undefined) return named;
    if (name.startsWith("#x") || name.startsWith("#X")) {
      const code = Number.parseInt(name.slice(2), 16);
      return isDecodableCodePoint(code) ? String.fromCodePoint(code) : match;
    }
    if (name.startsWith("#")) {
      const code = Number.parseInt(name.slice(1), 10);
      return isDecodableCodePoint(code) ? String.fromCodePoint(code) : match;
    }
    return match;
  });
}

/**
 * Derive the text/plain twin of a rich-text HTML body.
 *
 * The bulk (SES) channel sends multipart with a text/plain part, and the
 * character counter in the builder shows the length the author actually
 * typed rather than the length inflated by markup. Both need "what does
 * this say without the tags".
 *
 * List items become "• " lines because a bulleted list flattened into an
 * undifferentiated run of sentences is the one lossy case a reader would
 * actually notice. DECISION: numbered lists also render "• " rather than
 * reconstructed "1. / 2." — tracking per-<ol> counters through a regex
 * pass means tracking nesting too, and getting that subtly wrong in the
 * text/plain twin is worse than a bullet that's honest about being a
 * list item.
 */
export function richTextToPlainText(html: string): string {
  if (!html) return "";
  const text = html
    // Drop anything script-shaped outright rather than un-tagging it, so
    // its CONTENTS never surface as visible text.
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    // A link's URL must survive into the text part — "Renew here" with
    // no address is a dead CTA for every text-mode reader. Renders as
    // the familiar "text (url)" form. Skipped when the link text IS the
    // url (a pasted bare address) — "url (url)" helps nobody.
    .replace(
      /<a\b[^>]*\bhref="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi,
      (_m, href: string, inner: string) => {
        const innerText = inner.replace(/<[^>]+>/g, "").trim();
        return innerText === href ? inner : `${inner} (${href})`;
      },
    )
    .replace(/<(ul|ol)\b[^>]*>/gi, "\n")
    .replace(/<\/(ul|ol)>/gi, "\n")
    .replace(/<li\b[^>]*>/gi, "\n• ")
    .replace(/<\/li>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    // BOTH edges of a paragraph break the line. The editor can leave a
    // bare text node before the first <p> ("line one<p>line two</p>"),
    // and with only the close tag mapped, the open tag would strip
    // silently and fuse the two lines in the text part. The extra
    // newlines from a normal <p>…</p><p>…</p> chain collapse below.
    .replace(/<p\b[^>]*>/gi, "\n\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<[^>]+>/g, "");
  return decodeEntities(text)
    .replace(/[ \t]+/g, " ")
    .replace(/[ \t]*\n[ \t]*/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Visible-character length of an authored body, for the builder's
 * character counter and the "is there any content here" check.
 *
 * An HTML body's raw length includes markup the author never typed, so
 * counting it would tell someone with 200 visible characters that they'd
 * used 600. This counts what they'd see.
 */
export function richTextVisibleLength(
  body: string,
  format: RichTextBodyFormat,
): number {
  return format === "html" ? richTextToPlainText(body).length : body.length;
}

/**
 * True when a body carries no visible content — an empty editor often
 * leaves behind structural markup ("<p><br></p>") that is not nothing by
 * string length but is nothing to a reader. The engine treats a
 * content-free body as "no inline content" so it falls through to the
 * saved-template path instead of mailing an empty message.
 */
export function isRichTextEmpty(
  body: string,
  format: RichTextBodyFormat,
): boolean {
  if (format !== "html") return body.trim() === "";
  return richTextToPlainText(body).trim() === "";
}
